import axios from "axios";
import { API_URL } from "../utils/api-config";
import type {
  PermissionAction,
  PermissionMatrixEntry,
  PermissionModule,
} from "../config/permissions";

export type SuperAdminOrganization = {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  settings?: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type SuperAdminRole = {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  permissions: PermissionMatrixEntry[];
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
};

export type SuperAdminUser = {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  organizationId: string;
  role?: string;
  roleId?: string;
  roleName?: string;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
};

const baseUrl = `${API_URL}/super-admin`;

export type CreateOrganizationPayload = {
  name: string;
  slug?: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  settings?: Record<string, unknown>;
  admin?: {
    username: string;
    adminEmail: string;
    password: string;
    firstName?: string;
    lastName?: string;
  };
};

export type UpdateOrganizationPayload = Partial<
  Omit<CreateOrganizationPayload, "admin">
> & {
  isActive?: boolean;
};

export type CreateRolePayload = {
  name: string;
  description?: string;
  permissions: Partial<PermissionMatrixEntry>[];
};

export type UpdateRolePayload = Partial<
  Omit<CreateRolePayload, "permissions">
> & {
  permissions?: Partial<PermissionMatrixEntry>[];
};

export type CreateUserPayload = {
  username: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  roleId?: string;
  isActive?: boolean;
};

export type UpdateUserPayload = {
  roleId?: string;
  isActive?: boolean;
  username?: string;
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
};

const SuperAdminService = {
  async fetchOrganizations(): Promise<SuperAdminOrganization[]> {
    const { data } = await axios.get<SuperAdminOrganization[]>(
      `${baseUrl}/organizations`
    );
    return data;
  },

  async createOrganization(payload: CreateOrganizationPayload) {
    const { data } = await axios.post(`${baseUrl}/organizations`, payload);
    return data;
  },

  async updateOrganization(
    organizationId: string,
    payload: UpdateOrganizationPayload
  ) {
    const { data } = await axios.put(
      `${baseUrl}/organizations/${organizationId}`,
      payload
    );
    return data;
  },

  async deactivateOrganization(organizationId: string) {
    const { data } = await axios.delete(
      `${baseUrl}/organizations/${organizationId}`
    );
    return data;
  },

  async fetchRoles(organizationId: string): Promise<SuperAdminRole[]> {
    const { data } = await axios.get<SuperAdminRole[]>(
      `${baseUrl}/organizations/${organizationId}/roles`
    );
    return data;
  },

  async createRole(organizationId: string, payload: CreateRolePayload) {
    const { data } = await axios.post(
      `${baseUrl}/organizations/${organizationId}/roles`,
      payload
    );
    return data;
  },

  async updateRole(roleId: string, payload: UpdateRolePayload) {
    const { data } = await axios.put(`${baseUrl}/roles/${roleId}`, payload);
    return data;
  },

  async deleteRole(roleId: string) {
    const { data } = await axios.delete(`${baseUrl}/roles/${roleId}`);
    return data;
  },

  async fetchUsers(organizationId: string): Promise<SuperAdminUser[]> {
    const { data } = await axios.get<SuperAdminUser[]>(
      `${baseUrl}/organizations/${organizationId}/users`
    );
    return data;
  },

  async createUser(organizationId: string, payload: CreateUserPayload) {
    const { data } = await axios.post(
      `${baseUrl}/organizations/${organizationId}/users`,
      payload
    );
    return data;
  },

  async updateUser(userId: string, payload: UpdateUserPayload) {
    const { data } = await axios.patch(`${baseUrl}/users/${userId}`, payload);
    return data;
  },

  async deleteUser(userId: string) {
    const { data } = await axios.delete(`${baseUrl}/users/${userId}`);
    return data;
  },
};

export default SuperAdminService;
